import { Fragment, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Grid,
  Input,
  OutlineDangerButton,
  SecondaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import {
  type CivilDate,
  compareCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { formatCivilDateSlash } from '~/models/civilDate'
import { GridLabel } from '~/pages/CustomerInfo/components/FormField'
import {
  KW_PERIODS_MAX_COUNT,
  dayBefore,
} from '~/pages/CustomerInfo/models/kwPeriods'

/** 最初の期間より後の契約電力の期間 1 行 (入力中の値)。 `key` は行の同一性 (開始日を直しても変わらない)。 */
export type KwRow = { key: string; start: CivilDate | undefined; kw: string }

type Props = {
  /** 最初の期間の開始日 = 供給開始日 (契約編集の供給開始日に連動する)。 */
  supplyStart: CivilDate | undefined
  supplyEnd: CivilDate | undefined
  /** 「契約kW」のラベル文言 (最初に出る行の左に置く)。 */
  label: string
  firstKw: string
  rows: KwRow[]
  onFirstKwChange: (kw: string) => void
  onRowsChange: (rows: KwRow[]) => void
}

type Entry = {
  key: string
  start: CivilDate | undefined
  kw: string
  isFirst: boolean
  end: CivilDate | undefined
}

/** 過去の期間がこれ以上あれば畳む。 */
const COLLAPSE_FROM = 2

/** 1 期間 1 行の列: 適用開始日 / 「から」 / kW 入力 / 単位 / 取り消す。 行どうしで列が揃う。 期間の終わりは次の行の開始日で分かる。 */
const ROW_COLUMNS = '12rem max-content 8rem max-content 1fr'

let rowSeq = 0
const newKey = (): string => `kw-row-${(rowSeq += 1)}`

/**
 * 契約電力の期間の表 (`FormGrid` の行として出す: 左セル = ラベル / 右セル = 1 期間)。 最初の期間 (開始日 =
 * 供給開始日) は kW だけ直せる。 以降の期間は適用開始日と kW を直せ、 取り消せる。 期間は次の期間の開始日の
 * 前日までで、 並びは開始日順。 終わった期間が多い時は畳む。
 */
export const KwPeriodsEditor = ({
  supplyStart,
  supplyEnd,
  label,
  firstKw,
  rows,
  onFirstKwChange,
  onRowsChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [showPast, setShowPast] = useState(false)
  const today = todayCivilDate()

  const later = [...rows].sort((a, b) =>
    a.start === undefined
      ? 1
      : b.start === undefined
        ? -1
        : compareCivilDate(a.start, b.start),
  )
  const starts: Array<{
    key: string
    start: CivilDate | undefined
    kw: string
    isFirst: boolean
  }> = [
    { key: 'first', start: supplyStart, kw: firstKw, isFirst: true },
    ...later.map((r) => ({ ...r, isFirst: false })),
  ]
  const entries: Entry[] = starts.map((e, i) => {
    const next = starts[i + 1]?.start
    return { ...e, end: next === undefined ? supplyEnd : dayBefore(next) }
  })
  const isPast = (e: Entry): boolean =>
    e.end !== undefined && compareCivilDate(e.end, today) < 0
  const pastCount = entries.filter(isPast).length
  const collapse = pastCount >= COLLAPSE_FROM && !showPast
  const visible = collapse ? entries.filter((e) => !isPast(e)) : entries

  const update = (key: string, patch: Partial<KwRow>): void =>
    onRowsChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  // ラベル (「契約kW」) は最初に出る行の左セルに置く。 他の行の左セルは空
  const labelCell = (isFirstRow: boolean): React.ReactNode =>
    isFirstRow ? (
      <GridLabel htmlFor="customer-info-edit-kw">{label}</GridLabel>
    ) : (
      <span />
    )
  const atMax = rows.length >= KW_PERIODS_MAX_COUNT
  const hasToggle = pastCount >= COLLAPSE_FROM

  return (
    <>
      {hasToggle && (
        <>
          {labelCell(true)}
          <div>
            <SecondaryButton
              onClick={() => setShowPast(!showPast)}
              size="sm"
              type="button"
            >
              {showPast
                ? t('page:customerInfo.kwPeriod.hidePast')
                : t('page:customerInfo.kwPeriod.showPast', {
                    count: pastCount,
                  })}
            </SecondaryButton>
          </div>
        </>
      )}
      {visible.map((e, index) => (
        <Fragment key={e.key}>
          {labelCell(!hasToggle && index === 0)}
          <Grid
            alignItems="center"
            gridColumnGap={2}
            gridTemplateColumns={ROW_COLUMNS}
          >
            {e.isFirst ? (
              <Text as="span" color="Text.highEmphasis" fontSize="md">
                {e.start === undefined ? '' : formatCivilDateSlash(e.start)}
              </Text>
            ) : (
              <CompactCivilDateField
                id={`customer-info-edit-kw-start-${e.key}`}
                onChange={(value) => update(e.key, { start: value })}
                value={e.start}
              />
            )}
            <Text as="span" color="Text.highEmphasis" fontSize="md">
              {t('page:customerInfo.kwPeriod.from')}
            </Text>
            <Input
              aria-label={t('page:customerInfo.kwPeriod.kwOf', {
                from:
                  e.start === undefined ? '' : formatCivilDateSlash(e.start),
              })}
              id={e.isFirst ? 'customer-info-edit-kw' : undefined}
              inputMode="decimal"
              onChange={(ev) =>
                e.isFirst
                  ? onFirstKwChange(ev.target.value)
                  : update(e.key, { kw: ev.target.value })
              }
              value={e.kw}
            />
            <Text as="span" color="Text.highEmphasis" fontSize="md">
              kW
            </Text>
            {e.isFirst ? (
              <span />
            ) : (
              <div>
                <OutlineDangerButton
                  onClick={() =>
                    onRowsChange(rows.filter((r) => r.key !== e.key))
                  }
                  size="sm"
                  type="button"
                >
                  {t('page:customerInfo.kwPeriod.cancel')}
                </OutlineDangerButton>
              </div>
            )}
          </Grid>
        </Fragment>
      ))}
      <span />
      <div>
        <ButtonTooltip
          content={
            atMax ? t('page:customerInfo.kwPeriod.addDisabledAtMax') : undefined
          }
          focusable
        >
          <SecondaryButton
            disabled={atMax}
            onClick={() =>
              onRowsChange([
                ...rows,
                { key: newKey(), start: undefined, kw: '' },
              ])
            }
            size="sm"
            type="button"
          >
            {t('page:customerInfo.kwPeriod.add')}
          </SecondaryButton>
        </ButtonTooltip>
      </div>
    </>
  )
}
